import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field } from 'redux-form';
import { Alert, Spacing, Text } from '@reservamos/elements';
import { fieldRenderer } from 'utils/formRenderers';
import { When } from 'react-if';
import getDocTypeRegex from 'utils/getDocTypeRegex';
import normalizeRegex from 'utils/normalizeRegex';
import { documentId, required } from 'utils/formValidations';
import getPassengerAge from 'utils/getPassengerAge';
import MinorTravelModeField from 'components/MinorTravelModeField';
import useDebounce from 'hooks/useDebounce';
import { fetchClientDataByDocument } from '@/actions/purchase';
import { FLEXIBLE_EDITABLE_FIELDS, isAccountHolder } from '../../../utils/dotersFlexibleEditing';
import { trackFlexibleNameChange } from '../../../metrics/user-analytics/loyalty';
import { PhoneNumberField, GenderField, NationalityField } from '../../ReduxFields';
import normalizeName from '../../../utils/normalizeName';
import normalizeFirstName from '../../../utils/normalizeFirstName';
import PassengerBusCategories from './PassengerBusCategories';
import InfantDisclaimerText from './InfantDisclaimerText';
import DocumentTypeField from '../../DocumentTypeField';
import PassengerInsuranceCheckbox from '../../PassengerInsuranceCheckbox';
import LapChildField from './LapChildField';
import normalizeEmail from '../../../utils/normalizeEmail';
import LabelSwitch from '../../../ui/atoms/LabelSwitch';
import InfoText from '../../../ui/atoms/InfoText';
import DotersLoyaltyPassengerView from './DotersLoyaltyPassengerView';
import DotersGuestPurchaseSwitch from '../../../ui/molecules/DotersGuestPurchaseSwitch';
import BirthDateField from '../../BirthDateField';
import FileSelectField from '../FileSelectField';
import { getDoc2BrandMap, getDocumentTypeInstructioni18NKey } from '../../../utils/documentMaps';
import usePurchase from '../../../hooks/store/usePurchase';
import getFirstRowRestrictedCategories from '../../../utils/purchase/firstRowRestrictions';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import { walletTypeNeedsUserInPassenger } from '../../../utils/loyalty';
import useProviderPhoneDisplay from '../../../hooks/useProviderPhoneDisplay';
import usePassengerFieldDisabled from '../../../hooks/passengers/usePassengerFieldDisabled';
import 'styles/components/PersonPassengersForm';
/**
 * A component representing a form for a person passenger information.
 * @param {Object} props - Object props
 * @param {boolean} props.autofillEnabled - Indicates whether autofill is enabled.
 * @param {array} props.busCategories - An array of available bus categories.
 * @param {function} props.handleHolderEditing - A function to handle editing the holder.
 * @param {boolean} props.holderEditingEnabled - Indicates whether the holder editing is enabled.
 * @param {number} props.index - The index of the current passenger in the passenger list.
 * @param {number} props.insuranceIncomingUnitAmount - The unit amount of incoming insurance.
 * @param {number} props.insuranceOutgoingUnitAmount - The unit amount of outgoing insurance.
 * @param {boolean} props.isExchange - Indicates whether it is an exchange passenger form.
 * @param {boolean} props.isFirstPassenger - Indicates whether it is the first passenger.
 * @param {boolean} props.isLogged - Indicates whether the user is logged in.
 * @param {string} props.passenger - The passenger information to be displayed in the form.
 * @param {object} props.passengerFields - The passenger fields wit the values to be displayed in the form.
 * @param {boolean} props.showLoyaltyHolder - Indicates whether to show the loyalty holder field.
 * @param {array} props.updatingCategories - An array of updating categories.
 * @param {string} props.loyaltyHolderName - The name of the loyalty holder.
 * @param {array} props.initialFields - The initial fields to be displayed in the form.
 * @param {boolean} props.isOpenTicket - Indicates whether it is an open ticket.
 * @param {boolean} props.shouldShowPassengerInfoCard - Indicates whether to show the passenger info card.
 * @returns {JSX.Element} A React component that renders a form for a single person's passenger information.
 */
const PersonPassengersForm = ({
  autofillEnabled,
  busCategories,
  handleHolderEditing,
  holderEditingEnabled,
  index,
  insuranceIncomingUnitAmount,
  insuranceOutgoingUnitAmount,
  isExchange,
  isFirstPassenger,
  isLogged,
  passenger,
  passengerFields,
  showLoyaltyHolder,
  updatingCategories,
  isOpenTicket,
  loyaltyHolderName,
  initialFields,
  shouldShowPassengerInfoCard,
}) => {
  const formErrors = useSelector((state) => state.form.passengers?.syncErrors?.passengers?.[index]);
  const siemprePlus = useSelector((state) => state.siemprePlus);
  const { user, logged } = siemprePlus.toJS();
  const loyaltyName = logged ? user?.loyaltyName?.toLowerCase() : null;

  const dispatch = useDispatch();
  const prevDocumentIdRef = useRef(passengerFields.documentId);
  const prevDocumentTypeRef = useRef(passengerFields.documentType);
  const cpfAutoSelectFiredRef = useRef(false);
  const debouncedFields = useDebounce(passengerFields, 500);

  const { t } = useTranslation('passengers');
  const { features } = useSelector((state) => state.whitelabelConfig);
  const passengersDefaultCpf = features.FREE_FARE_DEFAULT_CPF;
  const {
    availableWallets = [],
    walletType,
    parentPassengers,
    passengers: purchasePassengers,
    departs,
    returns,
  } = usePurchase();

  const departureSeatNumber = passengerFields.seats?.departure?.number;
  const returnSeatNumber = passengerFields.seats?.return?.number;
  const restrictedTransporters = features.FIRST_ROW_RESTRICTED_TRANSPORTERS;
  const restrictedCategories = useMemo(() => {
    const ways = [
      { seatNumber: departureSeatNumber, way: departs },
      { seatNumber: returnSeatNumber, way: returns },
    ];
    const types = ways.reduce((acc, { seatNumber, way }) => {
      if (!seatNumber || !way) return acc;
      const restricted = getFirstRowRestrictedCategories({
        seatNumber,
        layout: way.layout,
        line: way.fragments?.[0]?.line,
        restrictedTransporters,
      });
      restricted.forEach((type) => acc.add(type));
      return acc;
    }, new Set());
    return Array.from(types);
  }, [departureSeatNumber, returnSeatNumber, departs, returns, restrictedTransporters]);
  const accountShouldBePassenger = walletTypeNeedsUserInPassenger(walletType);
  const shouldShowPhoneField = useProviderPhoneDisplay();

  const { userIsLoggedInWithAnyLoyaltyProgram } = useLoyaltyPrograms();
  const initialValues = initialFields.current ? initialFields.current[index] : passengerFields;

  // Factory function to create base isDisabled
  const createIsDisabled = usePassengerFieldDisabled({ isExchange, autofillEnabled });
  const baseIsDisabled = createIsDisabled(passengerFields, isFirstPassenger);

  const parentPassenger =
    isExchange && parentPassengers?.length > 0 ? parentPassengers[index] : null;

  const passengerAge = getPassengerAge(passengerFields.dateOfBirth);
  const phoneCountry = initialValues?.phoneCountry || '';
  const isMinor = typeof passengerAge === 'number' && passengerAge < 18;

  const {
    ENABLE_USER_AUTOFILL_BY_DNI: enableUserAutoFillByDNI,
    SHOW_DOCUMENT_TYPE_FIELD_AT: showDocumentTypeFieldAt,
  } = features;

  useEffect(() => {
    if (!enableUserAutoFillByDNI) return;

    const { documentId, documentType } = debouncedFields;

    if (formErrors?.documentId || formErrors?.documentType) return;

    // Compare current and previous values
    const prevDocumentId = prevDocumentIdRef.current;
    const prevDocumentType = prevDocumentTypeRef.current;

    if (documentId !== prevDocumentId || documentType !== prevDocumentType) {
      if (documentId && documentType === 'DNI') {
        dispatch(fetchClientDataByDocument(passenger, documentType, documentId));
      }

      prevDocumentIdRef.current = documentId;
      prevDocumentTypeRef.current = documentType;
    }
  }, [debouncedFields, formErrors, dispatch, passenger, enableUserAutoFillByDNI, initialValues]);

  /**
   * Auto-select CPF as the document type when the feature flag
   * `FREE_FARE_DEFAULT_CPF` is on for the active tenant and CPF is in
   * the tenant's passengers doc list.
   *
   * Set inside the form layer (rather than at initial-value computation
   * in `createPassenger`) so it survives hydration from purchase API
   * or local-storage frequent-passenger data that may have already populated
   * `documentType` with another value.
   *
   * The ref guard ensures we fire at most once per form lifetime,
   * so the effect cannot stomp on a manual user change if the flag value
   * flickers due to GrowthBook reconnects.
   */
  useEffect(() => {
    if (cpfAutoSelectFiredRef.current) return;
    if (!passengersDefaultCpf) return;
    if (passengerFields.documentType) {
      cpfAutoSelectFiredRef.current = true;
      return;
    }
    const passengersDocumentBrands = Object.values(getDoc2BrandMap('passengers'));
    if (!passengersDocumentBrands.includes('CPF')) return;

    cpfAutoSelectFiredRef.current = true;
    dispatch(change('passengers', `${passenger}.documentType`, 'CPF'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengersDefaultCpf]);

  // Determine if Doters flexible mode is enabled
  const isDotersFlexibleMode = features.DOTERS_FLEXIBLE_PASSENGER_MATCHING?.includes('redeem');

  const isDisabled = useCallback(
    ({ fieldValue, fieldName, isNameField = false }) => {
      if (isExchange && !fieldValue && fieldName === 'email') return false;

      // Determine the effective wallet type (walletType takes priority over loyaltyName)
      const effectiveWalletType = walletType || loyaltyName;

      // Check if this is a REAL name field that can be edited in Doters flexible mode
      // Only true name fields (firstName, lastName, etc.) should be treated as flexible editable
      // Document fields like documentId should NOT be treated as flexible editable, even if they simulate being firstName
      // IMPORTANT: This exception is ONLY for Doters wallet, not other loyalty programs
      const isFlexibleEditableField =
        loyaltyHolderName === 'doters' &&
        isDotersFlexibleMode &&
        isNameField &&
        FLEXIBLE_EDITABLE_FIELDS.includes(fieldName);

      // Component-specific loyalty disabling condition
      // For name fields in Doters flexible mode, only disable if toggle is NOT enabled
      // For other fields, disable based on standard loyalty conditions
      const loyaltyDisablingCondition =
        availableWallets?.includes(effectiveWalletType) &&
        isFirstPassenger &&
        fieldName !== 'busCategory' &&
        ((userIsLoggedInWithAnyLoyaltyProgram && accountShouldBePassenger) ||
          (!holderEditingEnabled && userIsLoggedInWithAnyLoyaltyProgram)) &&
        // For flexible editable name fields, only disable if:
        // - Flexible mode is enabled AND toggle is disabled (holderEditingEnabled = false)
        // Otherwise, use standard loyalty disabling
        // This exception only applies to Doters (checked in isFlexibleEditableField)
        !(isFlexibleEditableField && holderEditingEnabled);

      return baseIsDisabled(fieldValue, fieldName) || loyaltyDisablingCondition;
    },
    [
      baseIsDisabled,
      walletType,
      loyaltyName,
      availableWallets,
      isExchange,
      isFirstPassenger,
      loyaltyHolderName,
      isDotersFlexibleMode,
      userIsLoggedInWithAnyLoyaltyProgram,
      accountShouldBePassenger,
      holderEditingEnabled,
    ],
  );

  const canUseEditSwitch = {
    costapass: false,
    siempreplus: true,
    // Doters doesn't use showEditNameSwitch, uses DotersGuestPurchaseSwitch instead
  };

  /**
   * Check if the field should be visible based on the
   * feature flags and the passenger category.
   *
   * If the feature flag specified by fieldPerPassenger is not enabled,
   * the field should not be visible.
   *
   * If the feature flag specified by showFieldExcludingAdults is
   * enabled, the field should not be visible if the passenger selected the general category.
   *
   * @param {boolean} fieldPerPassenger - Indicates if the field is per passenger.
   * @param {boolean} showFieldExcludingAdults - Indicates if the field should be shown excluding adults.
   * @returns {boolean} True if the field should be visible, false otherwise.
   */
  const isFieldAllowedForCategory = (fieldPerPassenger, showFieldExcludingAdults) => {
    if (!fieldPerPassenger) return false;

    if (showFieldExcludingAdults) {
      return !passengerFields.busCategory.includes('general');
    }

    return true;
  };

  /**
   * Render the birthdate fields if either the
   * birth date per passenger or the nationality per passenger
   * feature flags are enabled or not.
   *
   * @returns {JSX.Element} The birth date fields
   */
  const renderBirthDateFields = () => (
    <When condition={features.BIRTH_DATE_PER_PASSENGER || features.NATIONALITY_PER_PASSENGER}>
      <Spacing
        size="S"
        flexGrow
        isResponsive
        alignItems="baseline"
        mobileAlign="inherit"
        responsiveColumnReverse
      >
        <When
          condition={isFieldAllowedForCategory(
            features.BIRTH_DATE_PER_PASSENGER,
            features.SHOW_BIRTH_DATE_EXCLUDING_ADULTS,
          )}
        >
          <Field
            id={`${passenger}.dateOfBirth`}
            name={`${passenger}.dateOfBirth`}
            passengerType={passengerFields.category}
            component={BirthDateField}
          />
        </When>
        <When condition={features.NATIONALITY_PER_PASSENGER}>
          <NationalityField name={`${passenger}.nationality`} />
        </When>
        <When condition={!features.BIRTH_DATE_PER_PASSENGER || !features.NATIONALITY_PER_PASSENGER}>
          <div />
        </When>
      </Spacing>
    </When>
  );

  /**
   * Render the file list field, and add spacing around it.
   *
   * @returns {JSX.Element} The file list field.
   */
  const renderFileSelectField = () => {
    const dividerStyles = {
      width: '100%',
      height: '17px',
    };

    return (
      <>
        <div style={dividerStyles} />
        <Spacing
          size="M"
          flexGrow
          isResponsive
          alignItems="baseline"
          mobileAlign="inherit"
          responsiveColumnReverse
        >
          <Field
            name={`${passenger}.document`}
            id={`${passenger}.document`}
            type="file"
            passengerType={passengerFields.category}
            placeholder={t('choose_file')}
            component={FileSelectField}
          />
        </Spacing>
        <div style={dividerStyles} />
      </>
    );
  };

  const exchangeInsuranceValidation =
    !isExchange || (isExchange && features.CAN_ADD_EXCHANGE_INSURANCE);

  // Show edit name switch only for loyalty programs that support it
  const showEditNameSwitch =
    showLoyaltyHolder &&
    !isExchange &&
    (canUseEditSwitch[loyaltyHolderName] ?? false) &&
    isFirstPassenger;

  const showSecondFirstName = features.SECOND_FIRST_NAME_PER_PASSENGER;
  const docTypeRegex = getDocTypeRegex('passengers', passengerFields.documentType);
  const docTypeRegexSecond = getDocTypeRegex(
    'passengersSecond',
    passengerFields.documentTypeSecond,
  );

  const secondLastNamePlaceholder = `${t('family_name_two')}${
    features.SECOND_LAST_NAME_REQUIRED_FOR_DNI && passengerFields.documentType === 'DNI'
      ? ''
      : ` ${t('optional')}`
  }`;

  /**
   * Returns a function to clear a form field if it's not disabled.
   * @param {string} fieldName - Name of the field to clear.
   * @param {boolean} [isNameField=false] - Indicates if this is a real name field type.
   * @returns {(Function|null)} Function to clear the field or null if disabled.
   */
  const handleClearField = (fieldName, isNameField = false) => {
    return isDisabled({
      fieldValue: initialValues[fieldName],
      fieldName,
      isNameField,
    })
      ? null
      : () => dispatch(change('passengers', `${passenger}.${fieldName}`, ''));
  };

  /**
   * Handler for name field changes in flexible mode
   * Tracks analytics when user changes name fields
   * Only tracks analytics for first passenger (index 0)
   */
  const handleNameFieldChange = ({ fieldName, value, passengerIndex }) => {
    const isDotersFlexible = features?.DOTERS_FLEXIBLE_PASSENGER_MATCHING?.includes('redeem');

    // Only track for the first passenger when flexible mode is enabled
    if (!isDotersFlexible || !holderEditingEnabled || passengerIndex !== 0) return;

    trackFlexibleNameChange({
      passengerIndex,
      fieldName,
      value,
      walletType,
      isCleared: !value || value.trim() === '',
    });
  };

  /**
   * Renders the document type and ID input fields if the location matches `documentTypeFieldLocation`.
   *
   * Includes a dropdown for document type, an input for document ID, and optional instructions
   * based on the document type. Returns `null` if the location does not match.
   *
   * @param {Object} props - Component props.
   * @param {string} props.location - Current location to validate rendering.
   *
   * @returns {JSX.Element|null} The document fields or `null` if not applicable.
   */
  const renderDocumentTypeField = ({ location }) => {
    if (location !== showDocumentTypeFieldAt || !features.IDENTIFICATION_DOCUMENT_PER_PASSENGER)
      return null;

    return (
      <Spacing size="S" flexGrow isResponsive>
        <DocumentTypeField
          id={`${passenger}.documentType`}
          name={`${passenger}.documentType`}
          dataValueRef={`${passenger}.documentId`}
          cls="form-item-wrap-l"
          passengerNationality={passengerFields.nationality}
          passengerDocumentType={passengerFields.documentType}
          documentIdValue={passengerFields.documentId}
          target="passengers"
          index={index}
          isDisabled={isDisabled({
            fieldValue: initialValues.documentType,
            fieldName: 'firstName',
            isNameField: false,
          })}
        />
        <div className="form-item-wrap-r">
          <Spacing size="S" vertical>
            <Field
              id={`${passenger}.documentId`}
              name={`${passenger}.documentId`}
              type="text"
              placeholder={t('document_number')}
              normalize={(input) => normalizeRegex(docTypeRegex, input)}
              component={fieldRenderer}
              onClickClear={handleClearField('documentId', false)}
              isDisabled={isDisabled({
                fieldValue: initialValues.documentId,
                fieldName: 'firstName',
                isNameField: false,
              })}
            />
            {getDocumentTypeInstructioni18NKey(passengerFields.documentType) ? (
              <Text size="XS" color="accent">
                {t(getDocumentTypeInstructioni18NKey(passengerFields.documentType))}
              </Text>
            ) : null}
          </Spacing>
        </div>
      </Spacing>
    );
  };

  const shouldHideInsuranceCheckbox =
    (isExchange && features.EXCHANGE_HIDE_APPLIED_INSURANCE && parentPassenger?.wantsInsurance) ||
    features.USE_NEW_INSURANCE_TOGGLE_PER_PASSENGER ||
    !exchangeInsuranceValidation ||
    !features.PASSENGERS_INSURANCE_ENABLED ||
    !(insuranceIncomingUnitAmount > 0 || insuranceOutgoingUnitAmount > 0);

  /**
   * Determine if the insurance checkbox should be disabled.
   * It is disabled for exchange passengers if the parent passenger already has insurance.
   * Round trip purchases returns wantsInsurance in parentPassenger for the specific trip being exchanged.
   *
   * It can be hidden with features.EXCHANGE_HIDE_APPLIED_INSURANCE = true, but if shown, it should be disabled.
   */
  const isInsuranceDisabled = isExchange && parentPassenger?.wantsInsurance;

  /**
   * Handles the confirmation from DotersGuestPurchaseSwitch
   * Updates holder editing state based on user's modal confirmation
   * @param {boolean} isEnabled - Whether guest purchase mode is enabled
   */
  const handleGuestPurchaseConfirm = (isEnabled) => {
    handleHolderEditing({ target: { checked: isEnabled } });
  };

  /**
   * Renders DotersGuestPurchaseSwitch when Doters flexible redeem mode applies to the first passenger.
   */
  const shouldShowDotersGuestSwitch = () => {
    if (
      isExchange ||
      !isDotersFlexibleMode ||
      !isFirstPassenger ||
      !isLogged ||
      loyaltyHolderName !== 'doters'
    ) {
      return null;
    }

    const passengerDiffersFromHolder =
      isAccountHolder({
        purchase: { passengers: purchasePassengers },
        user,
      }) === false;
    const switchInitialChecked = holderEditingEnabled || passengerDiffersFromHolder;

    return (
      <DotersGuestPurchaseSwitch
        onConfirm={handleGuestPurchaseConfirm}
        initialChecked={switchInitialChecked}
      />
    );
  };

  return (
    <>
      {renderDocumentTypeField({ location: 'start' })}
      <>
        {shouldShowPassengerInfoCard ? (
          <>
            <DotersLoyaltyPassengerView
              passenger={passenger}
              passengerFields={passengerFields}
              isFirstPassenger={isFirstPassenger}
              busDisabled={isDisabled({
                fieldValue: initialValues.busCategory,
                fieldName: 'busCategory',
                isNameField: false,
              })}
              initialValues={initialValues}
            />
          </>
        ) : (
          <>
            <div className="person-passengers-form">
              <Field
                autoComplete="given-name"
                name={`${passenger}.firstName`}
                id={`${passenger}.firstName`}
                type="text"
                placeholder={showSecondFirstName ? t('first_fore_name') : t('fore_name')}
                component={fieldRenderer}
                normalize={normalizeFirstName}
                onClickClear={handleClearField('firstName', true)}
                isDisabled={isDisabled({
                  fieldValue: initialValues.firstName,
                  fieldName: 'firstName',
                  isNameField: true,
                })}
                onChange={(e) => {
                  handleNameFieldChange({
                    fieldName: 'firstName',
                    value: e.target?.value || '',
                    passengerIndex: index,
                  });
                }}
              />
              <When condition={showSecondFirstName}>
                <Field
                  autoComplete="second-first-name"
                  name={`${passenger}.secondFirstName`}
                  id={`${passenger}.secondFirstName`}
                  type="text"
                  placeholder={`${t('second_fore_name')} ${t('optional')}`}
                  component={fieldRenderer}
                  normalize={normalizeName}
                  onClickClear={handleClearField('secondFirstName', true)}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.secondFirstName,
                    fieldName: 'secondFirstName',
                    isNameField: true,
                  })}
                  onChange={(e) => {
                    handleNameFieldChange({
                      fieldName: 'secondFirstName',
                      value: e.target?.value || '',
                      passengerIndex: index,
                    });
                  }}
                />
              </When>
              <Field
                autoComplete="family-name"
                name={`${passenger}.lastName`}
                id={`${passenger}.lastName`}
                type="text"
                placeholder={t('family_name_one')}
                component={fieldRenderer}
                normalize={normalizeName}
                onClickClear={handleClearField('lastName', true)}
                isDisabled={isDisabled({
                  fieldValue: initialValues.lastName,
                  fieldName: 'lastName',
                  isNameField: true,
                })}
                onChange={(e) => {
                  handleNameFieldChange({
                    fieldName: 'lastName',
                    value: e.target?.value || '',
                    passengerIndex: index,
                  });
                }}
              />
              {features.PASSENGERS_EXTRA_FIELDS && (
                <Field
                  autoComplete="do-not-autofill"
                  name={`${passenger}.secondLastName`}
                  id={`${passenger}.secondLastName`}
                  type="text"
                  placeholder={secondLastNamePlaceholder}
                  component={fieldRenderer}
                  normalize={normalizeName}
                  onClickClear={handleClearField('secondLastName', true)}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.secondLastName,
                    fieldName: 'secondLastName',
                    isNameField: true,
                  })}
                  onChange={(e) => {
                    handleNameFieldChange({
                      fieldName: 'secondLastName',
                      value: e.target?.value || '',
                      passengerIndex: index,
                    });
                  }}
                />
              )}
            </div>

            {(features.PASSENGERS_EXTRA_FIELDS || features.SHOW_PASSENGERS_EMAIL_FIELD) && (
              <Spacing size="S" flexGrow isResponsive>
                <Field
                  name={`${passenger}.email`}
                  id={`${passenger}.email`}
                  type="email"
                  placeholder={`${t('email')}${index !== 0 ? ` ${t('optional')}` : ''}`}
                  normalize={normalizeEmail}
                  component={fieldRenderer}
                  onClickClear={handleClearField('email', false)}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.email,
                    fieldName: 'email',
                    isNameField: false,
                  })}
                />
              </Spacing>
            )}

            {!features.SHOW_BIRTH_DATE_BELOW_BUS_CATEGORIES && renderBirthDateFields()}

            {showEditNameSwitch && (
              <LabelSwitch
                label={t('edit_name')}
                justifyContent="flex-end"
                checked={holderEditingEnabled}
                onChange={handleHolderEditing}
              />
            )}

            {renderDocumentTypeField({ location: 'end' })}

            {features.IDENTIFICATION_SECOND_DOCUMENT_PER_PASSENGER && (
              <Spacing size="S" flexGrow>
                <DocumentTypeField
                  id={`${passenger}.documentTypeSecond`}
                  name={`${passenger}.documentTypeSecond`}
                  dataValueRef={`${passenger}.documentIdSecond`}
                  cls="form-item-wrap-l"
                  passengerNationality={passengerFields.nationality}
                  passengerDocumentType={passengerFields.documentTypeSecond}
                  documentIdValue={passengerFields.documentIdSecond}
                  index={index}
                  target="passengersSecond"
                  validate={required}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.documentTypeSecond,
                    fieldName: 'firstName',
                    isNameField: false,
                  })}
                />
                <div className="form-item-wrap-r">
                  <Spacing size="S" vertical>
                    <Field
                      id={`${passenger}.documentIdSecond`}
                      name={`${passenger}.documentIdSecond`}
                      type="text"
                      placeholder={t('document_number')}
                      normalize={(input) => normalizeRegex(docTypeRegexSecond, input)}
                      component={fieldRenderer}
                      validate={documentId}
                      onClickClear={handleClearField('documentIdSecond', false)}
                      isDisabled={isDisabled({
                        fieldValue: initialValues.documentIdSecond,
                        fieldName: 'firstName',
                        isNameField: false,
                      })}
                    />
                    {getDocumentTypeInstructioni18NKey(passengerFields.documentType) ? (
                      <Text size="XS" color="accent">
                        {t(getDocumentTypeInstructioni18NKey(passengerFields.documentType))}
                      </Text>
                    ) : null}
                  </Spacing>
                </div>
              </Spacing>
            )}

            {(shouldShowPhoneField || features.GENDER_PER_PASSENGER || features.MINOR_RULES) && (
              <Spacing size="S" flexGrow isResponsive>
                <div className="person-passengers-form">
                  <When condition={shouldShowPhoneField}>
                    <PhoneNumberField
                      autoComplete="tel-national"
                      isDisabled={isDisabled({
                        fieldValue: null,
                        fieldName: 'phone',
                        isNameField: false,
                      })}
                      phoneCountryFieldName={`${passenger}.phoneCountry`}
                      phoneCodeFieldName={`${passenger}.phoneCode`}
                      phoneFieldName={`${passenger}.phone`}
                      formName="passengers"
                      placeholder={t('phone')}
                      cls="form-item-wrap-l"
                      initCountry={phoneCountry}
                    />
                  </When>
                  <When condition={isMinor && features.MINOR_RULES}>
                    <MinorTravelModeField
                      age={passengerAge}
                      name={`${passenger}.minorTravelMode`}
                    />
                  </When>
                  <When condition={features.GENDER_PER_PASSENGER}>
                    <GenderField name={`${passenger}.gender`} />
                  </When>
                </div>
              </Spacing>
            )}

            <PassengerBusCategories
              name={`${passenger}.busCategory`}
              passengerCategory={passengerFields.category}
              isFirstPassenger={isFirstPassenger}
              busCategories={busCategories}
              isUpdating={updatingCategories}
              isDisabled={isDisabled({
                fieldValue: initialValues.busCategory,
                fieldName: 'busCategory',
                isNameField: false,
              })}
              isExchange={isExchange}
              passengerBusCategory={passengerFields.busCategory}
              isOpenTicket={isOpenTicket}
              initialBusCategory={initialValues.busCategory}
              restrictedCategories={restrictedCategories}
            />

            {features.SHOW_BIRTH_DATE_BELOW_BUS_CATEGORIES && renderBirthDateFields()}

            {isFieldAllowedForCategory(
              features.DOCUMENT_PER_PASSENGER,
              features.SHOW_DOCUMENT_EXCLUDING_ADULTS,
            ) && renderFileSelectField()}

            {loyaltyHolderName &&
              holderEditingEnabled &&
              isFirstPassenger &&
              !walletTypeNeedsUserInPassenger(loyaltyHolderName) && (
                <InfoText>{t(`${loyaltyHolderName}_disclaimer`)}</InfoText>
              )}

            {!shouldHideInsuranceCheckbox && (
              <PassengerInsuranceCheckbox
                passenger={passenger}
                insuranceOutgoingUnitAmount={insuranceOutgoingUnitAmount}
                insuranceIncomingUnitAmount={insuranceIncomingUnitAmount}
                disabled={isInsuranceDisabled}
              />
            )}

            <When condition={features.LAP_CHILD_PER_PASSENGER}>
              <LapChildField
                passenger={passenger}
                passengerFields={passengerFields}
                index={index}
              />
            </When>
          </>
        )}

        {shouldShowDotersGuestSwitch()}
        <When
          condition={
            !shouldShowPassengerInfoCard && features.INFANTS_DISCLAIMER_PER_PASSENGER && isMinor
          }
        >
          <Alert
            alertType="warning"
            title={
              <InfantDisclaimerText ulStyle={{ listStyleType: 'disc', paddingLeft: '20px' }} />
            }
            weightTitle="regular"
            titleSize="S"
            iconSize="S"
          />
        </When>
      </>
    </>
  );
};

PersonPassengersForm.propTypes = {
  autofillEnabled: PropTypes.bool.isRequired,
  busCategories: PropTypes.object.isRequired,
  handleHolderEditing: PropTypes.func.isRequired,
  holderEditingEnabled: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  insuranceIncomingUnitAmount: PropTypes.number.isRequired,
  insuranceOutgoingUnitAmount: PropTypes.number.isRequired,
  isExchange: PropTypes.bool.isRequired,
  isFirstPassenger: PropTypes.bool.isRequired,
  isLogged: PropTypes.bool.isRequired,
  passenger: PropTypes.object.isRequired,
  passengerFields: PropTypes.object.isRequired,
  showLoyaltyHolder: PropTypes.object.isRequired,
  updatingCategories: PropTypes.bool.isRequired,
  isOpenTicket: PropTypes.bool,
  loyaltyHolderName: PropTypes.string,
  initialFields: PropTypes.array,
  shouldShowPassengerInfoCard: PropTypes.bool,
};

PersonPassengersForm.defaultProps = {};

export default PersonPassengersForm;
